import { action } from "typesafe-actions";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { AnyAction } from "redux";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { talkvitApi } from "../../api/talkvitAPI";
import {
  IMessageSequence,
  IMessageSequenceStep,
} from "./MessageSequence.types";

// ======= GET SEQUENCE LIST =======
export const FETCH_SEQUENCE_LIST_PROGRESS = "FETCH_SEQUENCE_LIST_PROGRESS";
export const FETCH_SEQUENCE_LIST_SUCCESS = "FETCH_SEQUENCE_LIST_SUCCESS";
export const FETCH_SEQUENCE_LIST_FAILED = "FETCH_SEQUENCE_LIST_FAILED";

export const fetchSequenceListProgress = () =>
  action(FETCH_SEQUENCE_LIST_PROGRESS);
export const fetchSequenceListSuccess = (
  data: IMessageSequence[],
  totalRecords: number
) => action(FETCH_SEQUENCE_LIST_SUCCESS, { data, totalRecords });
export const fetchSequenceListFailed = () =>
  action(FETCH_SEQUENCE_LIST_FAILED);

export const fetchMessageSequenceListAsync =
  (
    companyOutletUuid: string,
    pageNo: number,
    itemPerPage: number,
    status?: string
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchSequenceListProgress());
    try {
      const response = await talkvitApi.get(
        `/sequence/get-sequence?company_outlet_uuid=${companyOutletUuid}&pageNo=${pageNo}&itemPerPage=${itemPerPage}${status ? `&status=${status}` : ""}`
      );
      const result = response.data?.data?.result || [];
      dispatch(
        fetchSequenceListSuccess(result, response.data?.currentRecords || 0)
      );
    } catch (error: any) {
      dispatch(fetchSequenceListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message ||
            "Failed to fetch message sequences",
          displayAs: "snackbar",
        })
      );
    }
  };

// ======= GET SEQUENCE STEPS =======
export const FETCH_SEQUENCE_STEPS_PROGRESS = "FETCH_SEQUENCE_STEPS_PROGRESS";
export const FETCH_SEQUENCE_STEPS_SUCCESS = "FETCH_SEQUENCE_STEPS_SUCCESS";
export const FETCH_SEQUENCE_STEPS_FAILED = "FETCH_SEQUENCE_STEPS_FAILED";

export const fetchSequenceStepsProgress = () =>
  action(FETCH_SEQUENCE_STEPS_PROGRESS);
export const fetchSequenceStepsSuccess = (
  data: IMessageSequenceStep[],
  totalRecords: number
) => action(FETCH_SEQUENCE_STEPS_SUCCESS, { data, totalRecords });
export const fetchSequenceStepsFailed = () =>
  action(FETCH_SEQUENCE_STEPS_FAILED);

export const fetchSequenceStepsAsync =
  (
    messageSequenceUuid: string,
    pageNo: number,
    itemPerPage: number
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    dispatch(fetchSequenceStepsProgress());
    try {
      const response = await talkvitApi.get(
        `/sequence/get-sequence-steps?message_sequence_uuid=${messageSequenceUuid}&pageNo=${pageNo}&itemPerPage=${itemPerPage}`
      );
      dispatch(
        fetchSequenceStepsSuccess(
          response.data?.data?.result || [],
          response.data?.currentRecords || 0
        )
      );
    } catch (error: any) {
      dispatch(fetchSequenceStepsFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message ||
            "Failed to fetch sequence steps",
          displayAs: "snackbar",
        })
      );
    }
  };

// ======= UPSERT SEQUENCE =======
export const upsertMessageSequence =
  (data: any, onCallback?: (success: boolean, data?: any) => void) =>
  async (dispatch: any) => {
    try {
      dispatch(saveLoaderProgress());
      const response = await talkvitApi.post("/sequence/create-sequence", data);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: data.message_sequence_uuid
            ? "Message sequence updated successfully"
            : "Message sequence created successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true, response.data);
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message ||
            "Failed to save message sequence",
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };

// ======= UPSERT SEQUENCE STEP =======
export const upsertMessageSequenceStep =
  (data: any, onCallback?: (success: boolean) => void) =>
  async (dispatch: any) => {
    try {
      dispatch(saveLoaderProgress());
      await talkvitApi.put("/sequence/create-sequence-steps", data);
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: "Sequence step saved successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true);
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message ||
            "Failed to save sequence step",
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };

// ======= TOGGLE SEQUENCE STATUS =======
export const toggleMessageSequence =
  (
    sequenceUuid: string,
    status: "ACTIVE" | "INACTIVE",
    onCallback?: (success: boolean) => void
  ) =>
  async (dispatch: any) => {
    try {
      dispatch(saveLoaderProgress());
      await talkvitApi.put(`/sequence/toggle-sequence/${sequenceUuid}`, { status });
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "success",
          message: "Sequence status updated successfully",
          displayAs: "snackbar",
        })
      );
      onCallback?.(true);
    } catch (error: any) {
      dispatch(saveLoaderCompleted());
      dispatch(
        showMessage({
          type: "error",
          message:
            error.response?.data?.message || "Failed to update status",
          displayAs: "snackbar",
        })
      );
      onCallback?.(false);
    }
  };

// ======= CLEAR =======
export const CLEAR_SEQUENCE_STATE = "CLEAR_SEQUENCE_STATE";
export const clearSequenceState = () => action(CLEAR_SEQUENCE_STATE);
